import { SizeContextProvider } from '@app/components/cms/context/SizeContext';
import { LookbookDetailsModalContextProvider } from '@app/components/lookbook/context/LookbookDetailsModalContext';
import { PageProductsContextProvider } from '@app/components/pages/context/PageProductsContext';
import { PageSections } from '@app/components/pages/page/PageSections';
import { LookbookProductDetailsModal } from '@app/components/products/ProductDetails/LookbookProductDetailsModal';
import { PageTitle } from '@app/components/various/PageTitle';
import { compiledPaths } from '@app/paths';
import { useLookbookPageBySlug } from '@app/services/hooks/lookbook/useLookbookPageBySlug';
import { useLookbookPageProducts } from '@app/services/hooks/lookbook/useLookbookPageProducts';
import { useCustomStylesLoader } from '@app/utils/hooks/cms/useCustomStylesLoader';
import { listStylesToRecord } from '@app/utils/normalize';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
  lookbookId: string;
  pageSlug: string;
  productId: string | undefined;
  variantId: string | undefined;
}

export const LookbookCmsPageContent = ({ lookbookId, pageSlug, productId, variantId }: Props) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useLookbookPageBySlug({ lookbookId, pageSlug });
  const sections = React.useMemo(() => data?.sections ?? [], [data]);
  const blocks = React.useMemo(() => sections.flatMap(section => section.blocks), [sections]);
  const buttonStyles = React.useMemo(() => listStylesToRecord(data?.buttonStyles ?? [], 'className'), [data]);
  const textStyles = React.useMemo(() => listStylesToRecord(data?.textStyles ?? [], 'className') ?? [], [data]);

  useCustomStylesLoader(textStyles, buttonStyles);

  const { data: productsData } = useLookbookPageProducts({ blocks, lookbookId });

  React.useEffect(() => {
    if (isError) {
      navigate({ pathname: compiledPaths.PAGE_NOT_FOUND({}) }, { replace: true });
    }
  }, [isError]);

  return (
    <PageTitle title={data?.pageTitle}>
      <PageProductsContextProvider products={productsData ?? {}} lookbookId={lookbookId}>
        <SizeContextProvider>
          <PageSections sections={sections} isLoading={isLoading} />
        </SizeContextProvider>
      </PageProductsContextProvider>
      <LookbookDetailsModalContextProvider lookbookId={lookbookId} productId={productId} variantId={variantId}>
        <LookbookProductDetailsModal hideArrows />
      </LookbookDetailsModalContextProvider>
    </PageTitle>
  );
};
